import { ApiError } from './errors';

/** Placeholder values of a problem's `params` (arrays joined, objects left out). */
export type ProblemParams = Record<string, string | number | null | undefined>;

/** The app's message for a catalogue key, or undefined when its catalogue has no text for it. */
export type ProblemLookup = (key: string, params: ProblemParams) => string | undefined;

let lookup: ProblemLookup | undefined;

/**
 * Lets problem texts follow the app's language (ADR 0031): call once at startup with the app's catalogue lookup
 * (`bindConsole` does it for the office and the portal; the RF app does it itself).
 */
export function setProblemLookup(fn: ProblemLookup | undefined) {
  lookup = fn;
}

/** A lookup over an i18n `t`, which answers a missing key with the key itself. */
export function problemLookupFrom(t: (key: string, params?: ProblemParams) => string): ProblemLookup {
  return (key, params) => {
    const text = t(key, params);
    return text === key ? undefined : text;
  };
}

/** The problem's stable code and params (ProblemDetails `code`, `params`). */
export function problemOf(e: ApiError): { code?: string; params: ProblemParams } {
  const body = (e.body ?? {}) as { code?: unknown; params?: unknown };
  const code = typeof body.code === 'string' ? body.code : e.code;
  const params: ProblemParams = {};
  if (body.params && typeof body.params === 'object' && !Array.isArray(body.params)) {
    for (const [k, v] of Object.entries(body.params as Record<string, unknown>)) {
      if (typeof v === 'string' || typeof v === 'number') params[k] = v;
      else if (Array.isArray(v))
        params[k] = v.filter((x) => typeof x === 'string' || typeof x === 'number').join(', ');
    }
  }
  return { code, params };
}

/**
 * The problem in the user's language: `problems.<code>` replaces the title and `problems.<code>.detail` the detail
 * (with the params filled in); without a translation the API's own title and detail stay. Undefined when the app has
 * no text for the code.
 */
export function translateProblem(e: ApiError): { title: string; detail?: string } | undefined {
  const { code, params } = problemOf(e);
  if (!code || !lookup) return undefined;
  const title = lookup(`problems.${code}`, params);
  if (title === undefined) return undefined;
  const detail = lookup(`problems.${code}.detail`, params);
  // A detail translation with a placeholder the problem did not fill would show "{name}": keep the API's detail then.
  return { title, detail: detail !== undefined && !/\{\w+\}/.test(detail) ? detail : e.body?.detail };
}
